import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckCircle, FiClock, FiUser, FiFileText } = FiIcons;

const TestInterface = () => {
  const { testId } = useParams();
  const [testData, setTestData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);

  useEffect(() => {
    // Simular carregamento do teste
    const mockTest = {
      id: testId,
      title: 'Avaliação de Personalidade DISC',
      description: 'Teste para identificar perfil comportamental',
      company: 'Clínica Psicológica Silva',
      questions: [
        {
          id: 1,
          text: 'Como você se comporta em situações de pressão?',
          type: 'multiple',
          options: [
            'Mantenho a calma e analiso as opções',
            'Fico ansioso mas consigo me concentrar',
            'Prefiro delegar para outros',
            'Busco soluções criativas rapidamente'
          ]
        },
        {
          id: 2,
          text: 'Qual palavra melhor descreve sua personalidade?',
          type: 'multiple',
          options: [
            'Determinado',
            'Sociável',
            'Paciente',
            'Analítico'
          ]
        },
        {
          id: 3,
          text: 'Em uma escala de 1 a 10, como você avalia sua capacidade de liderança?',
          type: 'scale',
          min: 1,
          max: 10
        }
      ]
    };
    
    setTestData(mockTest);
  }, [testId]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeElapsed(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNext = () => {
    if (currentQuestion < testData.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setIsCompleted(true);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!testData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheckCircle} className="text-2xl text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Teste Concluído!
          </h1>
          
          <p className="text-gray-600 mb-6">
            Obrigado por participar. Seus resultados foram enviados para análise.
          </p>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Tempo total:</span>
              <span className="font-medium">{formatTime(timeElapsed)}</span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm text-gray-600">Questões respondidas:</span>
              <span className="font-medium">{Object.keys(answers).length}/{testData.questions.length}</span>
            </div>
          </div>
          
          <p className="text-sm text-gray-500">
            Os resultados serão disponibilizados em breve.
          </p>
        </motion.div>
      </div>
    );
  }

  const question = testData.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / testData.questions.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{testData.title}</h1>
              <p className="text-sm text-gray-600">{testData.company}</p>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center">
                <SafeIcon icon={FiClock} className="mr-1" />
                {formatTime(timeElapsed)}
              </div>
              <div className="flex items-center">
                <SafeIcon icon={FiFileText} className="mr-1" />
                {currentQuestion + 1}/{testData.questions.length}
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="bg-gray-200 rounded-full h-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
                className="bg-blue-600 h-2 rounded-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {question.text}
          </h2>
          
          {question.type === 'multiple' && (
            <div className="space-y-3">
              {question.options.map((option, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => handleAnswer(question.id, option)}
                  className={`w-full p-4 text-left rounded-lg border-2 transition-colors ${
                    answers[question.id] === option
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {option}
                </motion.button>
              ))}
            </div>
          )}
          
          {question.type === 'scale' && (
            <div className="space-y-4">
              <div className="flex justify-between text-sm text-gray-600">
                <span>{question.min}</span>
                <span>{question.max}</span>
              </div>
              <input
                type="range"
                min={question.min}
                max={question.max}
                value={answers[question.id] || question.min}
                onChange={(e) => handleAnswer(question.id, parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="text-center">
                <span className="text-2xl font-bold text-blue-600">
                  {answers[question.id] || question.min}
                </span>
              </div>
            </div>
          )}
          
          <div className="flex justify-end mt-8">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleNext}
              disabled={!answers[question.id]}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-8 py-3 rounded-lg font-medium transition-colors"
            >
              {currentQuestion === testData.questions.length - 1 ? 'Finalizar' : 'Próxima'}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TestInterface;